import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import AgoraRTC, {
  IAgoraRTCClient,
  ICameraVideoTrack,
  IMicrophoneAudioTrack
} from 'agora-rtc-sdk-ng';
import VirtualBackgroundExtension from 'agora-extension-virtual-background';
import { ModulesApisService } from './modules-apis.service';

export type VirtualBackgroundMode = 'none' | 'blur' | 'image';

export interface CallState {
  active: boolean;
  minimized: boolean;
  connecting: boolean;
  videoConnected: boolean;
  callEndedByRemote: boolean;
  doctorName: string;
  isMuted: boolean;
  isVideoMuted: boolean;
  elapsed: number;
  routeId: string | null;
  virtualBackground: VirtualBackgroundMode;
  virtualBackgroundImage: string | null;
  virtualBackgroundBusy: boolean;
}

const INITIAL_STATE: CallState = {
  active: false,
  minimized: false,
  connecting: false,
  videoConnected: false,
  callEndedByRemote: false,
  doctorName: '',
  isMuted: false,
  isVideoMuted: false,
  elapsed: 0,
  routeId: null,
  virtualBackground: 'none',
  virtualBackgroundImage: null,
  virtualBackgroundBusy: false
};

@Injectable({ providedIn: 'root' })
export class VideoCallService {

  private client!: IAgoraRTCClient;
  private localAudioTrack!: IMicrophoneAudioTrack;
  private localVideoTrack!: ICameraVideoTrack;

  private readonly appId = '9574c9a0ff9d41aa842ef6cc89e2904f';
  private channel: any;
  private token: string | null = null;
  private isFrontCamera = true;
  private timerHandle: any;

  readonly state$ = new BehaviorSubject<CallState>({ ...INITIAL_STATE });

  /** Emitted whenever the UI should bring the call back into view (e.g. exit system Picture-in-Picture). */
  readonly returnRequested$ = new Subject<void>();

  private remoteContainerEl: HTMLElement | string = 'remote-container';
  private localContainerEl: HTMLElement | string = 'local-container';

  private readonly vbExtension = new VirtualBackgroundExtension();
  private vbProcessor: any = null;
  private vbPiped = false;
  private vbInitPromise: Promise<void> | null = null;

  /** Whether this browser can run the virtual background effect at all. */
  readonly virtualBackgroundSupported: boolean;

  constructor(
    private moduleApi: ModulesApisService,
    private zone: NgZone,
    private router: Router
  ) {
    this.virtualBackgroundSupported = this.tryCheckVbCompatibility();
    if (this.virtualBackgroundSupported) {
      AgoraRTC.registerExtensions([this.vbExtension]);
    }
  }

  private tryCheckVbCompatibility(): boolean {
    try {
      return this.vbExtension.checkCompatibility();
    } catch {
      return false;
    }
  }

  get value(): CallState {
    return this.state$.value;
  }

  private patch(partial: Partial<CallState>) {
    this.state$.next({ ...this.state$.value, ...partial });
  }

  /**
   * Lets the UI hand over live element references (instead of string ids) so Agora keeps
   * playing into the right node even after it's been moved into another document (system PiP).
   */
  registerContainers(remoteEl: HTMLElement, localEl: HTMLElement) {
    this.remoteContainerEl = remoteEl;
    this.localContainerEl = localEl;
  }

  async start(pageObj: any, routeId: string) {
    if (this.value.active && this.value.routeId === routeId) {
      this.restore();
      return;
    }
    if (this.value.active) {
      await this.endCall();
    }

    this.patch({
      ...INITIAL_STATE,
      active: true,
      connecting: true,
      doctorName: pageObj.doctorName,
      routeId
    });

    this.moduleApi
      .getAgoraVideoDetails(pageObj.tenantid, pageObj.docuserid, pageObj.ptuserid)
      .subscribe((videoData: any) => {
        this.channel = videoData.channelName;
        this.moduleApi.getPatientRTCToken(0, this.channel).subscribe(data => {
          this.token = data;
          this.initAgora();
        });
      });
  }

  private initAgora() {
    this.client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });

    this.client.on('user-published', async (user, mediaType) => {
      await this.client.subscribe(user, mediaType);

      if (mediaType === 'video') {
        setTimeout(() => user.videoTrack?.play(this.remoteContainerEl), 0);
        this.zone.run(() => this.patch({ videoConnected: true, connecting: false, callEndedByRemote: false }));
      }

      if (mediaType === 'audio') {
        user.audioTrack?.play();
      }
    });

    this.client.on('user-unpublished', () => { });

    this.client.on('connection-state-change', (curState) => {
      this.zone.run(() => {
        switch (curState) {
          case 'CONNECTED':
            this.patch({ connecting: false });
            this.startTimer();
            break;
          case 'DISCONNECTED':
            this.patch({ videoConnected: false });
            this.stopTimer();
            break;
          case 'RECONNECTING':
          case 'CONNECTING':
            this.patch({ videoConnected: false });
            break;
        }
      });
    });

    this.client.on('user-left', () => {
      this.zone.run(() => {
        this.patch({ callEndedByRemote: true, videoConnected: false });
        setTimeout(() => this.autoEndAfterRemoteLeft(), 3000);
      });
    });

    this.joinCall();
  }

  private async joinCall() {
    await this.client.join(this.appId, this.channel, this.token, null);

    const tracks = await AgoraRTC.createMicrophoneAndCameraTracks();
    this.localAudioTrack = tracks[0];
    this.localVideoTrack = tracks[1];

    setTimeout(() => this.localVideoTrack.play(this.localContainerEl), 0);

    await this.client.publish([this.localAudioTrack, this.localVideoTrack]);
  }

  private startTimer() {
    this.stopTimer();
    this.timerHandle = setInterval(() => {
      this.zone.run(() => this.patch({ elapsed: this.value.elapsed + 1 }));
    }, 1000);
  }

  private stopTimer() {
    if (this.timerHandle) {
      clearInterval(this.timerHandle);
      this.timerHandle = null;
    }
  }

  minimize() {
    if (this.value.active) {
      this.patch({ minimized: true });
    }
  }

  restore() {
    this.patch({ minimized: false });
  }

  async switchCamera() {
    const cameras = await AgoraRTC.getCameras();
    if (cameras.length < 2) {
      return;
    }
    this.isFrontCamera = !this.isFrontCamera;
    const selectedCamera = this.isFrontCamera ? cameras[0] : cameras[1];
    await this.localVideoTrack.setDevice(selectedCamera.deviceId);
  }

  toggleMute() {
    const muted = !this.value.isMuted;
    this.localAudioTrack?.setEnabled(!muted);
    this.patch({ isMuted: muted });
  }

  toggleVideo() {
    const muted = !this.value.isVideoMuted;
    this.localVideoTrack?.setEnabled(!muted);
    this.patch({ isVideoMuted: muted });
  }

  async setVirtualBackgroundBlur(degree: 1 | 2 | 3 = 2) {
    const processor = await this.ensureVirtualBackgroundProcessor();
    if (!processor) { return; }

    this.patch({ virtualBackgroundBusy: true });
    try {
      processor.setOptions({ type: 'blur', blurDegree: degree });
      await processor.enable();
      this.patch({ virtualBackground: 'blur', virtualBackgroundImage: null });
    } catch {
      // leave the previous background in place if the effect fails to apply
    } finally {
      this.patch({ virtualBackgroundBusy: false });
    }
  }

  async setVirtualBackgroundImage(imageUrl: string) {
    const processor = await this.ensureVirtualBackgroundProcessor();
    if (!processor) { return; }

    this.patch({ virtualBackgroundBusy: true });
    try {
      const img = await this.loadImage(imageUrl);
      processor.setOptions({ type: 'img', source: img, fit: 'cover' });
      await processor.enable();
      this.patch({ virtualBackground: 'image', virtualBackgroundImage: imageUrl });
    } catch {
      // image failed to load/decode — keep the previous background
    } finally {
      this.patch({ virtualBackgroundBusy: false });
    }
  }

  async clearVirtualBackground() {
    if (this.vbProcessor && this.vbPiped) {
      this.patch({ virtualBackgroundBusy: true });
      try { await this.vbProcessor.disable(); } catch { }
      this.patch({ virtualBackgroundBusy: false });
    }
    this.patch({ virtualBackground: 'none', virtualBackgroundImage: null });
  }

  private async ensureVirtualBackgroundProcessor(): Promise<any | null> {
    if (!this.virtualBackgroundSupported || !this.localVideoTrack) { return null; }

    if (!this.vbProcessor) {
      this.vbProcessor = this.vbExtension.createProcessor();
    }
    if (!this.vbInitPromise) {
      this.vbInitPromise = this.vbProcessor.init().catch((err: any) => {
        this.vbInitPromise = null;
        this.vbProcessor = null;
        throw err;
      });
    }
    try {
      await this.vbInitPromise;
    } catch {
      return null;
    }

    if (!this.vbPiped) {
      this.localVideoTrack.pipe(this.vbProcessor).pipe(this.localVideoTrack.processorDestination);
      this.vbPiped = true;
    }
    return this.vbProcessor;
  }

  private loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load background image'));
      img.src = url;
    });
  }

  private async endCall() {
    this.stopTimer();
    try { this.localVideoTrack?.unpipe(); } catch { }
    try { this.localAudioTrack?.close(); } catch { }
    try { this.localVideoTrack?.close(); } catch { }
    try { await this.client?.leave(); } catch { }
    if (this.vbProcessor) {
      try { await this.vbProcessor.release(); } catch { }
    }
    this.vbProcessor = null;
    this.vbPiped = false;
    this.vbInitPromise = null;
    this.token = null;
    this.channel = null;
    this.remoteContainerEl = 'remote-container';
    this.localContainerEl = 'local-container';
    this.patch({ ...INITIAL_STATE });
  }

  async hangup() {
    const wasOnJoinPage = this.router.url.startsWith('/join');
    await this.endCall();
    if (wasOnJoinPage) {
      this.router.navigateByUrl('/view-booked-appointment');
    }
  }

  private async autoEndAfterRemoteLeft() {
    if (!this.value.callEndedByRemote) {
      return;
    }
    const wasOnJoinPage = this.router.url.startsWith('/join');
    await this.endCall();
    if (wasOnJoinPage) {
      this.router.navigateByUrl('/view-booked-appointment');
    }
  }

  expand() {
    if (!this.value.routeId) { return; }
    const targetUrl = '/join/' + this.value.routeId;
    if (!this.router.url.startsWith(targetUrl)) {
      this.router.navigate(['/join', this.value.routeId]);
    }
    this.returnRequested$.next();
  }
}
